import { MediaType, MediaViewer, getTestAttributes, noop, setPdfWorkerSrc } from '@chillibean/component-library';
import { AssetPlaceholder, icons } from '@chillibean/component-library/legacy';
import PropTypes from 'prop-types';
import { useMemo } from 'react';

import { getApiErrorCode, getApiErrorMessage } from '../../api/apiUtils';
import { AssetFormsErrorWithLogger } from '../Forms/components/container/ErrorBoundary';
import AssetFormsPlaceholder from '../Forms/components/presentational/AssetFormsPlaceholder';

const DocumentType = {
  IMAGE: 'image',
  PDF: 'pdf',
};

const TranscodeStatus = {
  PENDING: 'pending',
  COMPLETED: 'complete',
  FAILED: 'failed',
};

const PlaceholderText = {
  MISSING: 'File missing',
  NOT_SUPPORTED: 'File type not supported',
  TRANSCODING_FAILED: 'Media transcoding failed',
  TRANSCODING_PENDING: 'Media transcode pending',
  NO_PERMISSION: 'You do not have permission to view this file',
};

export function AssetRenderer(props) {
  const {
    account = undefined,
    chapterDefinitions = undefined,
    chapters = undefined,
    data = undefined,
    error = undefined,
    loading = undefined,
    onChaptersSubmit = noop,
    onVideoPlay = noop,
    user = undefined,
    zoomOnWheel = undefined,
  } = props;

  // VARIABLES
  const hdVideo = data?.media?.hdVideo;
  const sdVideo = data?.media?.sdVideo;
  const { accountAdmin } = user || {};
  const mediaUrl = data?.media?.url;
  const isAssetUrlEmpty = () => mediaUrl === null;

  const isPDF = () => data?.documentType === DocumentType.PDF;

  const permissions = useMemo(() => {
    return {
      canManageChapters: user?.permissions?.includes('can_manage_chapters') || accountAdmin,
      canViewAndDownloadDocuments: user?.permissions?.includes('can_view_and_download_documents') || accountAdmin,
    };
  }, [user]);

  const getTranscodeStatus = () => {
    const isFailed = (status) => status === TranscodeStatus.FAILED;
    const hdStatus = hdVideo?.status;
    const sdStatus = sdVideo?.status;

    if (hdStatus === TranscodeStatus.COMPLETED || sdStatus === TranscodeStatus.COMPLETED) {
      return TranscodeStatus.COMPLETED;
    }
    if (isFailed(hdStatus) && isFailed(sdStatus)) {
      return TranscodeStatus.FAILED;
    }

    return TranscodeStatus.PENDING;
  };

  const renderAssetPlaceHolder = ({ icon = icons.imagePlaceholder(), text } = {}) => {
    return <AssetPlaceholder icon={icon} text={text} />;
  };

  const renderAudio = () => {
    if (isAssetUrlEmpty()) {
      return renderAssetPlaceHolder({ text: PlaceholderText.MISSING });
    }

    return (
      <MediaViewer
        media={{ type: MediaType.AUDIO }}
        options={{
          src: mediaUrl,
        }}
      />
    );
  };

  const renderTranscodeVideo = () => {
    const transcodeStatus = getTranscodeStatus();
    const videoSrc = Object.entries(data?.media).map((media) => {
      const [key, value] = media;

      return {
        label: key === 'hdVideo' ? 'hd' : 'sd',
        selected: key === 'hdVideo',
        src: value.url,
        type: 'video/mp4',
      };
    });

    switch (transcodeStatus) {
      case TranscodeStatus.COMPLETED:
        return (
          <MediaViewer
            eventHandlers={{ onPlaying: onVideoPlay }}
            loading={loading}
            media={{ type: MediaType.VIDEO }}
            options={{
              chapterOptions: {
                chapterDefinitions: chapterDefinitions,
                chapters: chapters,
                enabled: account?.plan_options.asset_chapters_enabled,
                onSave: onChaptersSubmit,
                readOnly: !permissions.canManageChapters,
              },
              fps: sdVideo?.frameRate,
              src: videoSrc,
              timecodeOptions: {
                enabled: account?.plan_options.video_timecode_enabled,
              },
            }}
          />
        );
      case TranscodeStatus.PENDING:
        return renderAssetPlaceHolder({ icon: icons.videoPlaceholder(), text: PlaceholderText.TRANSCODING_PENDING });
      default:
        return renderAssetPlaceHolder({
          icon: icons.triangleWarning(100, 86, '#A8AAAB'),
          text: PlaceholderText.TRANSCODING_FAILED,
        });
    }
  };

  const renderImage = () => {
    if (isAssetUrlEmpty()) {
      return renderAssetPlaceHolder({ text: PlaceholderText.MISSING });
    }

    if (!data.thumbnails?.large) {
      return renderAssetPlaceHolder({ text: PlaceholderText.TRANSCODING_PENDING });
    }

    return (
      <MediaViewer
        media={{ type: MediaType.IMAGE }}
        options={{
          src: data.thumbnails.large,
          zoomOnWheel: zoomOnWheel,
        }}
      />
    );
  };

  const renderDocument = () => {
    if (!permissions.canViewAndDownloadDocuments && isPDF()) {
      return renderAssetPlaceHolder({
        icon: icons.triangleWarning(100, 86, '#A8AAAB'),
        text: PlaceholderText.NO_PERMISSION,
      });
    }

    if (!isAssetUrlEmpty() && isPDF()) {
      setPdfWorkerSrc(new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url).toString());

      return (
        <MediaViewer
          media={{ extension: data.extension, type: MediaType.DOCUMENT }}
          options={{
            src: mediaUrl,
          }}
        />
      );
    }

    return renderAssetPlaceHolder();
  };

  if (loading || (!data && !error)) {
    return <AssetFormsPlaceholder layout="fetchAsset" {...getTestAttributes('viewer-loading')} />;
  }

  if (error) {
    const apiErrorMessage = getApiErrorMessage(error);

    const errorLog = {
      log_type: 'Forms_Error',
      log_message: 'Fetch API Error',
      error: apiErrorMessage,
    };

    return (
      <AssetFormsErrorWithLogger
        errorLog={errorLog}
        source={getApiErrorCode(error)}
        errorMessage={apiErrorMessage}
        displayNextSteps
      />
    );
  }

  if (data) {
    const mediaType = data?.mediaType;
    switch (mediaType) {
      case MediaType.AUDIO:
        return renderAudio();
      case MediaType.DOCUMENT:
        return renderDocument();
      case MediaType.IMAGE:
        return renderImage();
      case MediaType.VIDEO:
        return renderTranscodeVideo();
      default:
        return renderAssetPlaceHolder();
    }
  }
}

AssetRenderer.propTypes = {
  account: PropTypes.shape({
    plan_options: PropTypes.shape({
      asset_chapters_enabled: PropTypes.bool,
    }),
  }),
  chapterDefinitions: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  ),
  chapters: PropTypes.arrayOf(
    PropTypes.shape({
      chapterDefinitionId: PropTypes.number,
      color: PropTypes.string,
      endTime: PropTypes.number,
      id: PropTypes.string,
      name: PropTypes.string,
      startTime: PropTypes.number,
    }),
  ),
  data: PropTypes.shape({
    mediaType: PropTypes.string,
    documentType: PropTypes.string,
    media: PropTypes.oneOfType([
      PropTypes.shape({
        sdVideo: PropTypes.shape({
          url: PropTypes.string,
          progress: PropTypes.number,
          status: PropTypes.string,
          frameRate: PropTypes.number,
        }),
        hdVideo: PropTypes.shape({
          url: PropTypes.string,
          progress: PropTypes.number,
          status: PropTypes.string,
          frameRate: PropTypes.number,
        }),
      }),
      PropTypes.shape({
        url: PropTypes.string,
      }),
    ]),
    thumbnails: PropTypes.shape({
      large: PropTypes.string,
      small: PropTypes.string,
    }),
  }),
  error: PropTypes.shape({}),
  loading: PropTypes.bool,
  onChaptersSubmit: PropTypes.func,
  onVideoPlay: PropTypes.func,
  user: PropTypes.shape({
    permissions: PropTypes.arrayOf(PropTypes.string),
    accountAdmin: PropTypes.bool,
  }),
  zoomOnWheel: PropTypes.bool,
};

export default AssetRenderer;
